import { useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { flexRender, type Header } from '@tanstack/react-table'
import { ArrowDown, ArrowUp, ArrowUpDown } from 'lucide-react'
import { TableHead } from '@/components/ui/table'
import { cn } from '@/lib/utils'

/** Minimum column width in pixels */
const MIN_COLUMN_WIDTH = 60

interface DataTableHeaderProps<TData> {
  header: Header<TData, unknown>
  isDraggable?: boolean
  isResizable?: boolean
  isSticky?: boolean
  stickyStyle?: React.CSSProperties
}

export function DataTableHeader<TData>({
  header,
  isDraggable = true,
  isResizable = true,
  isSticky = false,
  stickyStyle,
}: DataTableHeaderProps<TData>) {
  // Check if any column in the table is currently being resized
  const isAnyColumnResizing = !!header.getContext().table.getState()
    .columnSizingInfo.isResizingColumn

  // Disable dragging when any column is being resized or column is sticky
  const canDrag = isDraggable && !isSticky && !isAnyColumnResizing

  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: header.id,
    disabled: !canDrag,
  })

  const isResizing = header.column.getIsResizing()
  const canSort = header.column.getCanSort()
  const sorted = header.column.getIsSorted()
  const SortIcon =
    sorted === 'asc' ? ArrowUp : sorted === 'desc' ? ArrowDown : ArrowUpDown
  const ariaSort =
    sorted === 'asc' ? 'ascending' : sorted === 'desc' ? 'descending' : 'none'
  const columnMeta = header.column.columnDef.meta as
    | { displayName?: unknown }
    | undefined
  const sortLabel =
    typeof columnMeta?.displayName === 'string'
      ? columnMeta.displayName
      : header.column.id

  const style = {
    // Use Translate instead of Transform to avoid scaling during drag
    transform: CSS.Translate.toString(transform),
    transition,
    width: Math.max(header.getSize(), MIN_COLUMN_WIDTH),
    minWidth: MIN_COLUMN_WIDTH,
    opacity: isDragging ? 0.8 : 1,
    position: 'relative' as const,
    zIndex: isDragging ? 1 : 0,
    ...stickyStyle,
  }

  return (
    <TableHead
      ref={setNodeRef}
      className={cn(
        'group select-none border-t',
        'first:rounded-tl-sm last:rounded-tr-sm',
        isDragging && 'shadow-md',
        isAnyColumnResizing && 'cursor-col-resize'
      )}
      style={{
        ...style,
        background: 'color-mix(in srgb, var(--primary) 8%, var(--background))',
      }}
      aria-sort={canSort ? ariaSort : undefined}
    >
      <div className="flex min-h-6 min-w-0 items-center gap-1">
        {header.isPlaceholder ? null : canDrag ? (
          <button
            ref={setActivatorNodeRef}
            type="button"
            {...attributes}
            {...listeners}
            aria-label={`Reorder ${sortLabel} column`}
            className="flex min-h-6 min-w-0 flex-1 cursor-grab items-center rounded-sm text-left outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 active:cursor-grabbing"
          >
            <span className="block min-w-0 flex-1 truncate">
              {flexRender(header.column.columnDef.header, header.getContext())}
            </span>
          </button>
        ) : canSort ? (
          <button
            type="button"
            className="flex min-h-6 min-w-0 flex-1 cursor-pointer items-center gap-1 text-left outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
            aria-label={`Sort by ${sortLabel}`}
            onPointerDown={(event) => {
              event.stopPropagation()
            }}
            onClick={(event) => {
              event.stopPropagation()
              header.column.toggleSorting(sorted === 'asc')
            }}
          >
            <span className="block min-w-0 truncate">
              {flexRender(header.column.columnDef.header, header.getContext())}
            </span>
            <SortIcon
              className={cn(
                'size-3.5 shrink-0',
                sorted ? 'text-primary' : 'text-primary/70'
              )}
              aria-hidden="true"
            />
          </button>
        ) : (
          <span className="block min-w-0 flex-1 truncate">
            {flexRender(header.column.columnDef.header, header.getContext())}
          </span>
        )}

        {canDrag && canSort && !header.isPlaceholder ? (
          <button
            type="button"
            className="inline-flex size-6 shrink-0 cursor-pointer items-center justify-center rounded-sm outline-none hover:bg-primary/10 focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1"
            aria-label={`Sort by ${sortLabel}`}
            onPointerDown={(event) => {
              event.stopPropagation()
            }}
            onClick={(event) => {
              event.stopPropagation()
              header.column.toggleSorting(sorted === 'asc')
            }}
          >
            <SortIcon
              className={cn(
                'size-3.5',
                sorted ? 'text-primary' : 'text-primary/70'
              )}
              aria-hidden="true"
            />
          </button>
        ) : null}
      </div>

      {/* Resize handle */}
      {isResizable && header.column.getCanResize() && (
        <div
          onDoubleClick={() => header.column.resetSize()}
          onMouseDown={(e) => {
            e.stopPropagation()
            header.getResizeHandler()(e)
          }}
          onTouchStart={(e) => {
            e.stopPropagation()
            header.getResizeHandler()(e)
          }}
          onPointerDown={(e) => e.stopPropagation()}
          className={cn(
            'absolute right-0 top-0 h-full w-1 cursor-col-resize select-none touch-none',
            'opacity-0 group-hover:opacity-100 hover:bg-primary/50',
            isResizing && 'opacity-100 bg-primary'
          )}
        />
      )}
    </TableHead>
  )
}
